import * as turf from '@turf/turf';
import { GeoJSONSource, LayerSpecification, LngLatLike, PaintSpecification } from 'mapbox-gl';

import { mapAlertRed, mapInfoBlue } from '@/theme';
import { lngLatToTurf } from '@/utils';

const GEOFENCE_BREACHED_FILL_COLOR = mapAlertRed;
const GEOFENCE_BREACHED_LINE_COLOR = mapAlertRed;
const GEOFENCE_BREACHED_FILL_OPACITY = 0.15;

const GEOFENCE_IN_FILL_COLOR = mapInfoBlue;
const GEOFENCE_IN_LINE_COLOR = mapInfoBlue;
const GEOFENCE_IN_FILL_OPACITY = 0.2;
export const GEOFENCE_LINE_WIDTH = 2;
export const GEOFENCE_LINE_DASHARRAY: [number, number] = [3, 2];

export const getGeofenceColors = (isBreached: boolean) => {
    return {
        fillColor: isBreached ? GEOFENCE_BREACHED_FILL_COLOR : GEOFENCE_IN_FILL_COLOR,
        fillOpacity: isBreached ? GEOFENCE_BREACHED_FILL_OPACITY : GEOFENCE_IN_FILL_OPACITY,
        lineColor: isBreached ? GEOFENCE_BREACHED_LINE_COLOR : GEOFENCE_IN_LINE_COLOR,
    };
};

export const createCircleGeometry = (center: LngLatLike, radius: number) => {
    const circleCenter = turf.point(lngLatToTurf(center));

    const circle = turf.circle(circleCenter, radius, {
        steps: 64,
        units: 'meters',
    });

    return circle;
};

export const createGeofenceGeometries = (center: LngLatLike, radius: number) => {
    const circleCenter = turf.point(lngLatToTurf(center));

    const circle = createCircleGeometry(center, radius);

    // Create radius line from center to 3 o'clock position
    const radiusEndPoint = turf.destination(circleCenter, radius, 90, { units: 'meters' });
    const radiusLine = turf.lineString([lngLatToTurf(center), radiusEndPoint.geometry.coordinates]);
    const radiusGrabber = turf.circle(radiusEndPoint.geometry.coordinates, 40, { steps: 64, units: 'meters' });

    // Create crossmark at center
    const crossSize = Math.max(radius / 15, 5);
    const crossVertical = turf.lineString([
        turf.destination(circleCenter, crossSize, 0, { units: 'meters' }).geometry.coordinates,
        turf.destination(circleCenter, crossSize, 180, { units: 'meters' }).geometry.coordinates,
    ]);
    const crossHorizontal = turf.lineString([
        turf.destination(circleCenter, crossSize, 90, { units: 'meters' }).geometry.coordinates,
        turf.destination(circleCenter, crossSize, 270, { units: 'meters' }).geometry.coordinates,
    ]);
    const crossmark = turf.featureCollection([crossVertical, crossHorizontal]);

    return { circle, radiusLine, crossmark, radiusGrabber };
};

export const addOrUpdateGeoJSONLayer = (
    map: mapboxgl.Map,
    sourceConfig: { sourceId: string; beforeId?: string },
    layerConfig: LayerSpecification,
    data: GeoJSON.Feature<GeoJSON.Geometry> | GeoJSON.FeatureCollection<GeoJSON.Geometry>
) => {
    if (map.getSource(sourceConfig.sourceId)) {
        (map.getSource(sourceConfig.sourceId) as GeoJSONSource).setData(data);
        Object.entries(layerConfig.paint ?? {}).forEach(([key, value]) => {
            if (map.getLayer(layerConfig.id)) {
                map.setPaintProperty(layerConfig.id, key as keyof PaintSpecification, value);
            }
        });
    } else {
        map.addSource(sourceConfig.sourceId, {
            type: 'geojson',
            data,
        });
        map.addLayer(layerConfig, sourceConfig?.beforeId);
    }
};

export const removeGeoJSONLayer = (map: mapboxgl.Map, layerId: string, sourceId: string) => {
    try {
        // Check if map style is loaded before attempting to access layers/sources
        // This prevents errors when the map is being destroyed
        if (!map.getStyle()) {
            return;
        }
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
        }
        if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
        }
    } catch {
        // Map may be in a destroyed state during unmount, ignore cleanup errors
    }
};
